import { useState } from "react"

import {
  useReportsTransportInvalidation,
  useSaveReportTransport,
  useUpdateReportTransport,
} from "@/reports"
import {
  Button,
  getUserFacingErrorMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@coval/ui"
import { Save } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import type { MetricViews } from "@/types/MetricViewConfig"

interface SaveMultiRunReportButtonProps {
  runIds: string[]
  simulationOutputIds?: string[]
  viewConfig: Record<string, unknown>
  metricConfig?: MetricViews
  existingReportId?: string | null
  onSaved?: (reportId: string) => void
  isDisabled?: boolean
}

const log = logger("SaveMultiRunReportButton")

export const SaveMultiRunReportButton: React.FC<
  SaveMultiRunReportButtonProps
> = ({
  runIds,
  simulationOutputIds = [],
  viewConfig,
  metricConfig,
  existingReportId,
  onSaved,
  isDisabled,
}) => {
  const { trackEvent } = useAnalytics()
  const [displayName, setDisplayName] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [savedId, setSavedId] = useState<string | null>(null)

  const saveMutation = useSaveReportTransport()
  const updateMutation = useUpdateReportTransport()
  const reportsInvalidation = useReportsTransportInvalidation()
  const isLoading = saveMutation.isPending || updateMutation.isPending

  const invalidateReportCaches = async (reportId: string) => {
    try {
      await Promise.all([
        reportsInvalidation.invalidateReport(reportId),
        reportsInvalidation.invalidateList(),
      ])
    } catch (error) {
      log.error("Failed to invalidate report caches", error)
    }
  }

  const handleSave = async () => {
    if (
      (runIds.length === 0 && simulationOutputIds.length === 0) ||
      isLoading
    ) {
      return
    }

    try {
      if (existingReportId) {
        await updateMutation.mutateAsync({
          report_id: existingReportId,
          run_ids: runIds,
          ...(simulationOutputIds.length > 0 && {
            simulation_output_ids: simulationOutputIds,
          }),
          view_config: viewConfig,
          metric_config: metricConfig,
        })
        await invalidateReportCaches(existingReportId)
        trackEvent(TRACKING_EVENTS.REPORTS.SAVED, {
          report_id: existingReportId,
          run_count: runIds.length,
          is_update: true,
        })
        onSaved?.(existingReportId)
      } else {
        const result = await saveMutation.mutateAsync({
          display_name: displayName || "Untitled Report",
          run_ids: runIds,
          ...(simulationOutputIds.length > 0 && {
            simulation_output_ids: simulationOutputIds,
          }),
          view_config: viewConfig,
          metric_config: metricConfig,
        })
        await invalidateReportCaches(result.ulid)
        trackEvent(TRACKING_EVENTS.REPORTS.SAVED, {
          report_id: result.ulid,
          run_count: runIds.length,
          is_update: false,
        })
        setSavedId(result.ulid)
        onSaved?.(result.ulid)
      }
    } catch (error) {
      log.error("Failed to save report", error)
      toast.danger({
        title: "Error",
        description: getUserFacingErrorMessage(error, {
          fallback: "Failed to save report.",
        }),
      })
    }
  }

  // For existing reports, just a direct save button (no popover)
  if (existingReportId) {
    return (
      <Button
        variant="secondary"
        aria-label="Save report"
        data-testid="save-report-button"
        onClick={() => {
          void handleSave()
        }}
        disabled={isLoading || isDisabled}
      >
        <Save className="h-4 w-4" />
        {isLoading ? "Saving…" : "Save"}
      </Button>
    )
  }

  // For new reports, show popover with name input
  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          setSavedId(null)
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          aria-label="Save report"
          data-testid="save-report-button"
        >
          <Save className="h-4 w-4" />
          Save
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" className="p-4 gap-3 w-80">
        <div className="flex flex-col w-full gap-3">
          {savedId ? (
            <div
              className="text-sm text-coval-success-text"
              data-testid="save-report-success"
            >
              Report saved!
            </div>
          ) : (
            <>
              <div className="text-sm font-medium">Save Report</div>
              <Input
                placeholder="Report name"
                aria-label="Report name"
                data-testid="save-report-name-input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    void handleSave()
                  }
                }}
                autoFocus
                disabled={isLoading}
              />
              <Button
                data-testid="save-report-confirm-button"
                onClick={() => {
                  void handleSave()
                }}
                disabled={
                  isLoading ||
                  (runIds.length === 0 && simulationOutputIds.length === 0)
                }
              >
                {isLoading ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
